import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { QASkeleton } from './QASkeleton';
import { QAEmpty } from './QAEmpty';
import type { AdviceDocRow } from '../../../shared/api';

const STATUS_COLOUR: Record<string, string> = {
  draft: 'qa-badge--mute', review: 'qa-badge--hold', approved: 'qa-badge--brass',
  sent: 'qa-badge--brass', signed: 'qa-badge--pass', declined: 'qa-badge--out',
};

export function AdviceTab({ clientId, canCreate, canApprove }: { clientId: string; canCreate: boolean; canApprove: boolean }) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [kind, setKind] = useState<'soa' | 'roa'>('soa');
  const [title, setTitle] = useState('Statement of Advice — Draft');

  const { data, isLoading } = useQuery({
    queryKey: ['client', clientId, 'advice'],
    queryFn: () => api<{ data: AdviceDocRow[] }>(`/api/clients/${clientId}/advice`),
  });

  const create = useMutation({
    mutationFn: () => api('/api/advice', {
      method: 'POST',
      body: JSON.stringify({ clientId, kind, title, objectives: 'TBD', currentSituation: 'TBD' }),
    }),
    onSuccess: () => { setCreating(false); qc.invalidateQueries({ queryKey: ['client', clientId, 'advice'] }); },
  });

  const transition = useMutation({
    mutationFn: ({ id, to }: { id: string; to: string }) =>
      api(`/api/advice/${id}/transition`, { method: 'POST', body: JSON.stringify({ to }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client', clientId, 'advice'] }),
  });

  if (isLoading) return <div className="mt-6"><QASkeleton variant="table" rows={4} /></div>;
  const rows = data?.data ?? [];

  return (
    <div className="mt-6">
      {canCreate && (
        <div className="mb-6">
          {creating ? (
            <section className="qa-card p-5">
              <p className="qa-eyebrow mb-3">New draft</p>
              <div className="flex gap-3 mb-3 flex-col sm:flex-row">
                <select value={kind} onChange={e => setKind(e.target.value as 'soa' | 'roa')} className="qa-select sm:w-32">
                  <option value="soa">SoA</option><option value="roa">RoA</option>
                </select>
                <input className="qa-input flex-1" value={title} onChange={e => setTitle(e.target.value)} />
              </div>
              <div className="flex justify-end gap-3">
                <button className="btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
                <button className="btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>
                  {create.isPending ? 'Creating…' : 'Create draft'}
                </button>
              </div>
              <p className="text-xs text-ink-mute mt-3">
                <strong>Disclaimer:</strong> generated drafts are placeholder text. A qualified adviser / compliance reviewer must approve before client delivery.
              </p>
            </section>
          ) : (
            <button className="btn-primary" onClick={() => setCreating(true)}>New advice draft</button>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <QAEmpty inline title="No advice documents yet." />
      ) : (
        <div className="overflow-x-auto border border-hairline">
          <table className="qa-table">
            <thead>
              <tr>
                <th>Title</th><th>Kind</th><th>Status</th>
                <th>Created</th><th>Signed</th><th>Template</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const nextStates: Record<string, string[]> = {
                  draft: ['review'],
                  review: canApprove ? ['approved', 'declined'] : ['draft'],
                  approved: canApprove ? ['sent', 'declined'] : [],
                  sent: ['signed', 'declined'],
                  signed: [], declined: [],
                };
                return (
                  <tr key={d.id}>
                    <td><a className="text-ink hover:text-rust" href={`/api/advice/${d.id}/preview`} target="_blank" rel="noreferrer">{d.title}</a>
                      <div className="text-xs mt-1"><a className="text-ink-mute hover:text-rust qa-mono" href={`/api/advice/${d.id}/pdf`} target="_blank" rel="noreferrer">↓ PDF</a></div></td>
                    <td>{d.kind.toUpperCase()}</td>
                    <td><span className={`qa-badge ${STATUS_COLOUR[d.status] ?? 'qa-badge--mute'}`}>{d.status.toUpperCase()}</span></td>
                    <td>{formatDateAU(d.created_at)}</td>
                    <td>{formatDateAU(d.signed_date)}</td>
                    <td className="qa-mono text-xs">{d.template_version}</td>
                    <td>
                      <div className="flex gap-2">
                        {(nextStates[d.status] ?? []).map(to => (
                          <button key={to} className="btn-ghost text-xs"
                            disabled={transition.isPending}
                            onClick={() => transition.mutate({ id: d.id, to })}>
                            → {to}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
